import React, { useMemo, useState } from "react";
import { Box, Card, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { getOrderOutcome } from "../labOrderOutcome";

type Range = "daily" | "weekly" | "monthly";

// Stack order bottom -> top; status colours match the order list chips
const SERIES = [
  { key: "fulfilled", label: "Fulfilled", color: "#098963" },
  { key: "open", label: "In progress", color: "#E39B1B" },
  { key: "cancelled", label: "Cancelled / refunded", color: "#D32F2F" },
] as const;

const RANGE_SUBTITLE: Record<Range, string> = {
  daily: "Last 7 days",
  weekly: "Last 8 weeks",
  monthly: "Last 12 months",
};

interface Bucket {
  label: string;
  start: Date;
  end: Date; // exclusive
  fulfilled: number;
  open: number;
  cancelled: number;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Consecutive periods ending with the current one
const buildBuckets = (range: Range): Bucket[] => {
  const today = startOfDay(new Date());
  const empty = { fulfilled: 0, open: 0, cancelled: 0 };

  if (range === "daily") {
    return Array.from({ length: 7 }, (_, i) => {
      const start = new Date(today);
      start.setDate(today.getDate() - (6 - i));
      const end = new Date(start);
      end.setDate(start.getDate() + 1);
      const label = start.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit" });
      return { label, start, end, ...empty };
    });
  }

  if (range === "weekly") {
    // Weeks start on Monday
    const thisWeek = new Date(today);
    thisWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    return Array.from({ length: 8 }, (_, i) => {
      const start = new Date(thisWeek);
      start.setDate(thisWeek.getDate() - (7 - i) * 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      const label = start.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      return { label, start, end, ...empty };
    });
  }

  return Array.from({ length: 12 }, (_, i) => {
    const start = new Date(today.getFullYear(), today.getMonth() - (11 - i), 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const label = start.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
    return { label, start, end, ...empty };
  });
};

const toggleSx = {
  gap: "0.2rem",
  backgroundColor: "#f5f5f5",
  borderRadius: "16px",
  padding: "6px",
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.85rem",
    color: "#555",
    padding: "6px 14px",
    "&:hover": { backgroundColor: "#e0e0e0" },
  },
  "& .MuiToggleButton-root.Mui-selected": {
    backgroundColor: "#098963",
    color: "white",
    "&:hover": { backgroundColor: "#098963" },
  },
};

const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as Bucket;
  const total = row.fulfilled + row.open + row.cancelled;
  return (
    <Box sx={{ bgcolor: "#fff", border: "1px solid #E4E4E4", borderRadius: 2, p: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#022219", mb: 0.5 }}>
        {label} · {total} {total === 1 ? "order" : "orders"}
      </Typography>
      {[...SERIES].reverse().map((s) => (
        <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: s.color }} />
          <Typography sx={{ fontSize: 12, color: "#55595A", flexGrow: 1 }}>{s.label}</Typography>
          <Typography sx={{ fontSize: 12, color: "#022219", fontWeight: 600, ml: 2 }}>{row[s.key]}</Typography>
        </Box>
      ))}
    </Box>
  );
};

interface LabOrdersChartProps {
  bookings: any[];
}

// Orders per period, stacked by outcome; built from the same bookings as the overview cards
export default function LabOrdersChart({ bookings }: LabOrdersChartProps) {
  const [range, setRange] = useState<Range>("weekly");

  const chartData = useMemo(() => {
    const buckets = buildBuckets(range);
    bookings.forEach((booking) => {
      // Bookings are placed in the period of their scheduled date
      const date = new Date(booking.scheduledDate);
      if (isNaN(date.getTime())) return;
      const bucket = buckets.find((b) => date >= b.start && date < b.end);
      if (!bucket) return;
      const outcome = getOrderOutcome(booking);
      bucket[outcome === "refund" ? "cancelled" : outcome]++;
    });
    return buckets;
  }, [bookings, range]);

  const totals = useMemo(
    () =>
      SERIES.reduce(
        (acc, s) => ({ ...acc, [s.key]: chartData.reduce((sum, b) => sum + b[s.key], 0) }),
        {} as Record<string, number>
      ),
    [chartData]
  );
  const grandTotal = SERIES.reduce((sum, s) => sum + totals[s.key], 0);

  return (
    <Card sx={{ p: 3, borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", width: "100%" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography fontSize={14} color="grey.600">
            Orders · {RANGE_SUBTITLE[range]}
          </Typography>
          <Typography fontSize={18} fontWeight={600} color="#022219">
            {grandTotal} {grandTotal === 1 ? "order" : "orders"}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(_, value) => value && setRange(value)}
          size="small"
          sx={toggleSx}
        >
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Legend with totals, so identity never relies on colour alone */}
      <Box display="flex" flexWrap="wrap" gap={2.5} mt={2}>
        {SERIES.map((s) => (
          <Box key={s.key} display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: s.color }} />
            <Typography fontSize={13} color="#55595A">
              {s.label} <Box component="span" sx={{ color: "#022219", fontWeight: 600 }}>{totals[s.key]}</Box>
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{ width: "100%", height: 300, mt: 2 }}
        role="img"
        aria-label={`Orders ${RANGE_SUBTITLE[range].toLowerCase()}: ${SERIES.map((s) => `${totals[s.key]} ${s.label.toLowerCase()}`).join(", ")}`}
      >
        {grandTotal === 0 ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" sx={{ color: "grey.500", textAlign: "center" }}>
            <Typography>No orders in this period</Typography>
            <Typography variant="caption">Try a longer range</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#EEEEEE" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} content={<ChartTooltip />} />
              {SERIES.map((s, i) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId="orders"
                  fill={s.color}
                  stroke="#fff"
                  strokeWidth={2}
                  maxBarSize={28}
                  radius={i === SERIES.length - 1 ? [4, 4, 0, 0] : 0}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
}
